import { createFileRoute, Navigate, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import {
  Toaster,
  SidebarProvider,
  SidebarInset,
  AllRightsReservedFooter,
  TopNavbar,
  useUserType,
} from '@org/ui';
import { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { AppSidebar } from '../components/app-sidebar';
import { AccessGate } from '../components/access/access-gate';
import { AccessBanner } from '../components/access/access-banner';
import { LimitedVeil } from '../components/access/limited-veil';
import { takeReturnTo } from '../lib/access';
import { StudentPicker } from '../components/student-picker';
import { NotificationBell } from '../components/notification-bell';
import { useTranslation } from 'react-i18next';
import { identifyUser, resetUser } from '../lib/analytics';

type AuthUser = ReturnType<typeof useAuth0>['user'];

// Pathless layout holding the Auth0 guard and the student shell (sidebar +
// top navbar). Every student route is a child, so their URLs are unchanged;
// public pages (e.g. /journal) live outside it, directly under the root.
function AppLayoutInner() {
  const { user } = useAuth0();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.sub) {
      identifyUser(user);
    } else {
      resetUser();
    }
  }, [user]);

  // Where the student was heading before the /welcome page sent them to
  // Google (the Auth0 provider has no redirect callback, so the landing page
  // keeps the path in sessionStorage and we pick it up here, once).
  useEffect(() => {
    const target = takeReturnTo(typeof window !== 'undefined' ? window.sessionStorage : null);
    if (target && target !== window.location.pathname) {
      void navigate({ href: target, replace: true });
    }
  }, [navigate]);

  return (
    <AccessGate>
      <SidebarProvider defaultOpen={true} className="bg-[#fff8fb]">
        <AppSidebar />
        <ShellContent user={user} />
      </SidebarProvider>
    </AccessGate>
  );
}

function ShellContent({ user }: { user: AuthUser }) {
  const { t } = useTranslation('admin');
  const { gender } = useUserType();
  // Feminine copy is the default; male students get the `_male` i18n context
  // variants (i18next falls back to the base key when the variant is absent).
  const genderContext = gender === 'male' ? 'male' : undefined;
  return (
    <SidebarInset className="bg-[#fff8fb]" aria-label="Main content">
      <div className="flex min-h-svh flex-col">
        <TopNavbar
          eyebrowText={t('p2e.layout.headerEyebrow', { context: genderContext })}
          displayName={
            user?.given_name ??
            user?.nickname ??
            user?.email ??
            t('p2e.layout.nameFallback', { context: genderContext })
          }
          user={user}
          app="p2e"
          extraElements={
            <>
              <StudentPicker />
              <NotificationBell />
            </>
          }
          logoutLabel={t('common.nav.signOut')}
          loggingOutLabel={t('common.nav.signingOut')}
        />
        <div className="flex-1 px-4 py-6 sm:px-6 lg:px-10" role="none">
          <div className="mx-auto w-full max-w-6xl">
            <AccessBanner />
            <LimitedVeil>
              <Outlet />
            </LimitedVeil>
          </div>
        </div>
        <AllRightsReservedFooter className="bg-[#fff8fb]" />
        <TanStackRouterDevtools />
        <Toaster closeButton />
      </div>
    </SidebarInset>
  );
}

// Logged-out visitors land on the public /welcome page (Google-only sign-in
// and sign-up) instead of being thrown straight into the Auth0 screen. The
// Auth0 SDK finishes its own redirect first (`isLoading`), so a fresh login
// bouncing back to `/` is never mistaken for an anonymous visit.
function AppLayout() {
  const isBypassEnabled = import.meta.env.VITE_E2E_AUTH_BYPASS === 'true';
  if (isBypassEnabled) return <AppLayoutInner />;
  return <GuardedAppLayout />;
}

function GuardedAppLayout() {
  const { t } = useTranslation('admin');
  const { isLoading, isAuthenticated } = useAuth0();
  const location = useRouterState({ select: (s) => s.location });

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <span>{t('common.nav.redirecting')}</span>
      </div>
    );
  }
  if (!isAuthenticated) {
    const returnTo = `${location.pathname}${location.searchStr ?? ''}`;
    return <Navigate to="/welcome" replace search={{ returnTo: returnTo === '/' ? undefined : returnTo }} />;
  }
  return <AppLayoutInner />;
}

export const Route = createFileRoute('/_app')({
  component: AppLayout,
});
